// <s-signup-popup> opens the sign-up dialog once per visitor after a delay,
// reopens it when the visitor returns from submitting its form, and copies the discount code.
const STORAGE_KEY = 'tatlim-signup-popup-seen';

class SSignupPopup extends HTMLElement {
  #ac;
  #timer;
  #dialog;

  connectedCallback() {
    this.#dialog = this.querySelector('dialog');
    if (!this.#dialog) return;

    this.#ac = new AbortController();
    const signal = this.#ac.signal;
    const form = this.querySelector('form');

    this.#dialog.addEventListener('close', () => this.#remember(), { signal });
    form?.addEventListener('submit', () => this.#remember(), { signal });

    // CommandEvent fires on the commandfor target (this element) and does not bubble.
    this.addEventListener('command', (event) => {
      if (event.command === '--copy') this.#copy(event.source);
    }, { signal });

    // Shopify returns to the page with the form id as the hash after a customer form posts.
    if (form?.id && window.location.hash === `#${form.id}`) {
      this.#open();
      return;
    }

    // In the editor, show on every load (after the same delay) so the pop-up stays reviewable.
    if (!this.hasAttribute('data-design-mode') && this.#seen()) return;

    const delay = Math.max(0, Number(this.dataset.delay) || 0) * 1000;
    this.#timer = setTimeout(() => this.#open(), delay);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    clearTimeout(this.#timer);
  }

  #open() {
    if (this.#dialog.open) return;
    // Wait while another modal (bag, menu, search) is open rather than stacking on top of it.
    if (document.querySelector('dialog:modal')) {
      this.#timer = setTimeout(() => this.#open(), 5000);
      return;
    }
    this.#dialog.showModal();
  }

  // In the editor, remember per session so the pop-up stays reviewable in Canvas.
  #storage() {
    try {
      return this.hasAttribute('data-design-mode') ? window.sessionStorage : window.localStorage;
    } catch {
      return null;
    }
  }

  #seen() {
    return this.#storage()?.getItem(STORAGE_KEY) === '1';
  }

  #remember() {
    this.#storage()?.setItem(STORAGE_KEY, '1');
  }

  async #copy(source) {
    if (!(source instanceof HTMLElement)) return;
    const code = source.dataset.code;
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
    } catch {
      return;
    }
    const label = source.dataset.copiedLabel;
    if (label) source.textContent = label;
  }
}

if (!customElements.get('s-signup-popup')) customElements.define('s-signup-popup', SSignupPopup);
